/*
 * @Description: 封装aegis数据上报
 * @usage:
 * @FilePath: /app-monorepo-projects/packages/app-share/src/libs/ageis/index.ts
 */
import { Metric as WebVitalsMetric, getLCP } from 'web-vitals';

import { IS_SSR } from '@/utils/env';
import { getAegisWithRetry } from '@/utils/script';
// import { parseUrl } from '@/utils/no-import-meta/url';
import { getBenchMark } from '@/utils/jsbridge';
import { getCookie } from '@/utils/cookie';
import { EAegisLogTypes } from '@/constants/aegis';
import getBrowserObj, { EPlatformTypes } from '@/utils/no-import-meta/get-browser-obj';
import type { ReportTimeLog, PagePerformanceLog } from '@/types/aegis';

import { beforeRequestManager } from './before-request';
import { getFirstScreenPerformance } from './page-performance';

let aegis: InstanceType<NonNullable<Window['Aegis']>> | null = null;
let actID = '';
let osType = '';
let measuredFCP = 0;
let hasReportedLCP = false;
let hasReportedFCP = false;
// 用来收集 web-vitals 库收集到的 LCP 指标
const webvitalsLCPMetrics: WebVitalsMetric[] = [];
// 页面测速枚举
enum EViseCustomedPerfMetrics {
  'firstPaint' = 'first-paint',
  'firstContentfulPaint' = 'first-contentful-paint',
}

// CSR 阶段才去获取 osType
if (!IS_SSR) {
  const browserInfo = getBrowserObj();
  osType = browserInfo.platform;
}

/**
 * 初始化加载 aegis 并实例化
 *
 * @export
 * @param {string} [id='']
 * @return {*}
 */
export default async function initAegisAndReportPef(id = '') {
  if (IS_SSR) return;

  // 分到另外的延时宏任务的微任务队列去执行
  await new Promise((resolve) => {
    setTimeout(resolve, 0);
  });

  // 以下只是为了使用 await 语法，对返回值没有期待
  // eslint-disable-next-line @typescript-eslint/no-misused-promises
  window.addEventListener('load', async () => {
    aegis = await getAegisIns();
    reportLCP({ isCalledWhenWindowOnload: true });
  });

  // 获取 aegis 实例
  aegis = await getAegisIns();
  if (!aegis) return;

  actID = id;
  reportFirstScreenPerformance();
  subscribeDocReadyStateChanged();
  reportFCP();
  reportLCP();
};

/**
 * 封装 aegis.reportTime 的调用
 *
 * @export
 * @param {*} params
 */
export async function aegisReportTime(params: ReportTimeLog) {
  const extraParams: Partial<ReportTimeLog> = {};
  if (osType === EPlatformTypes.android) {
    const { benchMark = '' } = (await getBenchMark()) || {};
    extraParams.ext3 = String(benchMark);
  }

  aegis?.reportTime({
    ...params,
    ...extraParams,
  });
}

async function reportFirstScreenPerformance() {
  const aegis = await getAegisIns();
  if (!aegis) return;

  const data = await getFirstScreenPerformance(window.Vise?.userData?.aegisPerfMaterial);
  if (!data) return;

  console.log('[aegis.sendPipeline]:', data);
  // 直接 hack 掉自动测速，手动上报自动测速的数据
  // 且原包上没有导出部分类型，所以这里使用 any 代替
  aegis!.sendPipeline([
    (log: PagePerformanceLog, resolve: any) => {
      const param: string[] = [];
      Object.entries(log).forEach(([key, value]) => {
        param.push(`${key}=${value}`);
      });
      const { performanceUrl = '' } = aegis!.config || {};
      const splitSymbol = performanceUrl!.indexOf('?') === -1 ? '?' : '&';
      return resolve({
        url: `${performanceUrl}${splitSymbol}${param.join('&')}`,
        type: EAegisLogTypes.PERFORMANCE,
        log,
      });
    },
  ], EAegisLogTypes.PERFORMANCE as any)(data);
}

/**
 * 订阅 document 的 readystatechange 事件
 *
 * @export
 */
export function subscribeDocReadyStateChanged() {
  if (IS_SSR) return;

  document.addEventListener('readystatechange', () => {
    if (document.readyState !== 'complete') return;

    aegisReportTime({
      name: 'fmp',
      duration: window.performance.now(),
      ext3: `fmp_${actID}`,
    });
  });
}

/**
 * 上报 LCP 数据
 *
 * 本方法可以被调用上报的场景：
 * 1. 在 window.onload 时调用
 * 2. 在非 window.onload 时调用，防止 window.onload 时间太长没触发，但是用户触发了下面第 2 点描述的 web-vitals 强制触发的场景
 *
 * web-vitals 库的 getLCP 方法调用用户传入的回调机制：
 * 1. 当其第二个参数 reportAllChanges 为 true 时，每次有 LCP 的 PeformanceEntry 进来之后都会调用
 * 2. 当其第二个参数 reportAllChanges 为 false 时，则只会调用一次，时机有如下几个：
 * （1）keydown 或者 click 后强制调用，调用之后取消掉 PerformanceObserver 的监听
 * （2）切换隐藏时强制调用，调用之后取消掉 PerformanceObserver 的监听
 * （3）pageshow 事件被触发且 evt.persisted 为真时被触发（指的是浏览器历史前进或后退），调用之后不会取消监听，每次有前进或后退都会调用
 *
 * @export
 * @param {*} [{ isCalledWhenWindowOnload = false }={}]
 * @return {*}  {void}
 */
export async function reportLCP({ isCalledWhenWindowOnload = false } = {}): Promise<void> {
  if (IS_SSR || hasReportedLCP) return;

  // 如果需要检测所有的 LCP PerformanceEntry 改变，则需要先清除掉之前的缓存
  if (isCalledWhenWindowOnload) {
    webvitalsLCPMetrics.splice(0);
  }

  // 缓存 metric 到数组中
  // 如果 monitorAllChanges 为 false，那么就会只返回一个 metric，只调用一次回调
  await new Promise<void>((resolve) => {
    getLCP((metric: WebVitalsMetric) => {
      webvitalsLCPMetrics.push(metric);
      resolve();
    }, isCalledWhenWindowOnload);
  });

  // 如果没有收集到或者后来在 pending 期间被上报过了，那就直接退出
  if (webvitalsLCPMetrics?.length <= 0 || hasReportedLCP) return;

  // 取最后一个进行上报
  const len = webvitalsLCPMetrics.length;
  const lastMetric = webvitalsLCPMetrics[len - 1];
  const { value = 0 } = lastMetric;
  aegisReportTime({
    name: 'lcp',
    duration: value,
    ext2: `lcp_${actID}`,
  });
  hasReportedLCP = true;
  console.log(`[reportLCP] value: ${value}; last webvitalsLCPMetric: ${JSON.stringify(lastMetric)}; isCalledWhenWindowOnload: ${isCalledWhenWindowOnload}`);
};

/**
 * 上报 FCP 数据
 *
 * @export
 * @return {*}
 */
export function reportFCP() {
  if (IS_SSR || hasReportedFCP) return;

  let isOriPefInsMetricsAdpoted = false;
  let fcpEntry = window.Vise.userData?.perfMetrics?.[EViseCustomedPerfMetrics.firstContentfulPaint];
  // 如果没有的话，通常是因为浏览器兼容性而拿不到 window,PerformanceObserver，那这里兜底用 window.performance
  if (!fcpEntry) {
    const oriFcpEntry = window.performance
      .getEntriesByType('paint')
      .filter(entry => entry.name === EViseCustomedPerfMetrics.firstContentfulPaint)?.[0];
    fcpEntry = oriFcpEntry?.toJSON?.();
    isOriPefInsMetricsAdpoted = true;
  };

  // 再没有的话，直接退出
  if (!fcpEntry) return;

  measuredFCP = Math.round(fcpEntry.startTime + fcpEntry.duration);
  if (!measuredFCP) return;

  // TODO：这里之所以区分两个 name，是因为源码就基本这样的逻辑，其实很不合理，需要统一！！！
  if (!isOriPefInsMetricsAdpoted) {
    aegisReportTime({
      name: 'fcp',
      duration: measuredFCP,
      ext1: `fcp_${actID}`,
    });
  }
  aegisReportTime({
    name: 'scriptFcp',
    duration: measuredFCP,
    ext1: `fcp_${actID}`,
  });
  hasReportedFCP = true;
};

/**
 * 获取 aegis 实例
 *
 * @export
 * @return {*}
 */
export async function getAegisIns() {
  if (IS_SSR) return null;

  if (aegis) return aegis;

  // eslint-disable-next-line @typescript-eslint/naming-convention
  const Aegis = await getAegisWithRetry();
  if (!Aegis) return null;

  aegis = new Aegis({
    // 项目 ID，即上报 key
    id: 'ydtrSdkMlFjpEaeBxq',
    // 用户唯一 ID（可选）
    uin: getCookie('person_id'),
    // 接口测速
    reportApiSpeed: true,
    // 静态资源测速
    reportAssetSpeed: true,
    // 0~1 抽样率
    random: 1,
    // 是否是单页应用
    spa: false,
    // 不需要页面自动测速，因为将首屏测速的部分提前到了 index.html 中，且手动上报，来避免在 head 中引入 aegis 脚本以阻塞首屏
    pagePerformance: false,
    // api 接口上报
    api: {
      // 上报接口入参和回包
      apiDetail: true,
      // 全量上报，不区分是否白名单
      reportRequest: true,
    },
    // 上报前钩子
    beforeRequest(data: { logs: any, logType: EAegisLogTypes }) {
      const canSubmit = beforeRequestManager.run(data);
      if (!canSubmit) return false;
      return data;
    },
  });
  return aegis;
}
